import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orpc, rpcClient } from '@/api/rpc/client';
import type {
  CreateApExceptionRuleSetInput,
  UpdateApExceptionRuleSetInput,
} from '@erp/rpc';

/** All rule sets in the org — the Exception Rules page and the node picker. */
export function useApExceptionRuleSets(options?: { enabled?: boolean }) {
  return useQuery({
    ...orpc.accountsPayable.exceptionRuleSets.list.queryOptions({ input: {} }),
    enabled: options?.enabled ?? true,
    staleTime: 30_000,
  });
}

/** One rule set with its rules, owner names, and enum options. */
export function useApExceptionRuleSet(id: string | null) {
  return useQuery({
    ...orpc.accountsPayable.exceptionRuleSets.get.queryOptions({
      input: { id: id ?? '' },
    }),
    enabled: id !== null,
  });
}

function useInvalidateRuleSets() {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({
      queryKey: orpc.accountsPayable.exceptionRuleSets.key(),
    });
}

export function useCreateApExceptionRuleSet() {
  const invalidate = useInvalidateRuleSets();
  return useMutation({
    mutationFn: (input: CreateApExceptionRuleSetInput) =>
      rpcClient.accountsPayable.exceptionRuleSets.create(input),
    onSuccess: () => void invalidate(),
  });
}

export function useUpdateApExceptionRuleSet() {
  const invalidate = useInvalidateRuleSets();
  return useMutation({
    mutationFn: (input: UpdateApExceptionRuleSetInput) =>
      rpcClient.accountsPayable.exceptionRuleSets.update(input),
    onSuccess: () => void invalidate(),
  });
}

export function useDeleteApExceptionRuleSet() {
  const invalidate = useInvalidateRuleSets();
  return useMutation({
    mutationFn: (id: string) =>
      rpcClient.accountsPayable.exceptionRuleSets.delete({ id }),
    onSuccess: () => void invalidate(),
  });
}
